import React, { useState, useEffect } from 'react';
import { permissionApi } from '../services/api';

interface Permission {
  id: number;
  name: string;
  action: string;
}

interface ModulePermissions {
  module: string;
  permissions: Permission[];
}

interface PermissionModuleSelectorProps {
  selectedPermissions: number[];
  onChange: (permissionIds: number[]) => void;
}

const PermissionModuleSelector: React.FC<PermissionModuleSelectorProps> = ({
  selectedPermissions,
  onChange,
}) => {
  const [modules, setModules] = useState<ModulePermissions[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setLoading(true);
        const response = await permissionApi.list();
        setModules(response.permissions);
      } catch (error) {
        console.error('Failed to load permissions:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPermissions();
  }, []);

  const handlePermissionToggle = (permissionId: number) => {
    const newSelected = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter((id) => id !== permissionId)
      : [...selectedPermissions, permissionId];
    onChange(newSelected);
  };

  const handleModuleToggle = (modulePermissions: Permission[], selectAll: boolean) => {
    const modulePermissionIds = modulePermissions.map((p) => p.id);
    if (selectAll) {
      // Add all module permissions
      const newSelected = [...new Set([...selectedPermissions, ...modulePermissionIds])];
      onChange(newSelected);
    } else {
      // Remove all module permissions
      const newSelected = selectedPermissions.filter((id) => !modulePermissionIds.includes(id));
      onChange(newSelected);
    }
  };

  const isModuleSelected = (modulePermissions: Permission[]) => {
    return modulePermissions.every((p) => selectedPermissions.includes(p.id));
  };

  const isModulePartiallySelected = (modulePermissions: Permission[]) => {
    const selectedCount = modulePermissions.filter((p) => selectedPermissions.includes(p.id)).length;
    return selectedCount > 0 && selectedCount < modulePermissions.length;
  };

  if (loading) {
    return <div className="text-center py-4 theme-text-secondary">Loading permissions...</div>;
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto theme-border border rounded-lg p-4 theme-bg-secondary">
      {modules.map((moduleData) => {
        const allSelected = isModuleSelected(moduleData.permissions);
        const partiallySelected = isModulePartiallySelected(moduleData.permissions);

        return (
          <div key={moduleData.module} className="border-b theme-border pb-4 last:border-b-0 last:pb-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold theme-text-primary capitalize">
                {moduleData.module.replace('_', ' ')}
              </h4>
              <button
                type="button"
                onClick={() => handleModuleToggle(moduleData.permissions, !allSelected)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-2 ml-4">
              {moduleData.permissions.map((permission) => (
                <label
                  key={permission.id}
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(permission.id)}
                    onChange={() => handlePermissionToggle(permission.id)}
                    className="rounded theme-border text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm theme-text-primary capitalize">{permission.action}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PermissionModuleSelector;

